import { PagedResponseDto } from "../../../../shared/dtos/paged-response.dto";
import { OrderGroup } from "../../../../shared/interface";
import { BaseService } from "../../../../shared/services";
import { buildFilterCondition, buildFilterExpression } from "../../../../shared/utils";
import { ITechOperationDto } from "../dtos/tech-operation.dto";
import { ICropReportFilter } from "../interfaces/crop-report-filter";

export type TFarmLandTechRequest = {
  farmId: string;
  seasonId: string;
  farmLandId: string;
  limit: number;
  offset: number;
  order: OrderGroup;
  startDate?: string;
  finishDate?: string;
};

class TechOperationsService extends BaseService<ITechOperationDto, never> {
  private path = "TechOperationsV2";

  async get(id: string): Promise<ITechOperationDto> {
    const url = [this.path, id].join("/");
    const dto = await super.request<ITechOperationDto>(url);
    return dto;
  }

  async list(
    request: TFarmLandTechRequest,
    options: { signal?: AbortSignal } = {}
  ): Promise<ITechOperationDto[]> {
    const { signal = null } = options;
    const response = await this.request<PagedResponseDto<ITechOperationDto>>(this.path, {
      method: "GET",
      searchParams: buildParams(request),
      signal,
    });
    return response.data;
  }

  async listCount(
    request: TFarmLandTechRequest,
    options: { signal?: AbortSignal } = {}
  ): Promise<{ count: number }> {
    const { signal = null } = options;
    const response = await this.request<PagedResponseDto<ITechOperationDto>>(this.path, {
      method: "GET",
      searchParams: { ...buildParams(request), pageNumber: 1, pageSize: 1 },
      signal,
    });
    return { count: response.meta.totalCount };
  }

  async getWeeklyFile(data: ICropReportFilter): Promise<void> {
    try {
      return this.downloadFileAsync("TechOpReports/getWeeklyFile", {
        searchParams: data,
      });
    } catch (e) {
      return Promise.reject(e);
    }
  }
}

function buildParams(request: TFarmLandTechRequest): Record<string, unknown> {
  const { farmId, seasonId, farmLandId, limit, offset, order, startDate, finishDate } =
    request;

  const pageNumber = Math.floor(offset / limit) + 1;
  const sort =
    order.length > 0
      ? order.map(([field, direction]) => `${String(field)} ${direction}`).join(", ")
      : undefined;
  const filter = buildFilterExpression(
    buildFilterCondition("farmId", "==", farmId),
    buildFilterCondition("seasonId", "==", seasonId),
    buildFilterCondition("farmLandId", "==", farmLandId),
    startDate && buildFilterCondition("startDate", ">=", startDate),
    finishDate && buildFilterCondition("finishDate", "<=", finishDate)
  );

  return { pageNumber, pageSize: limit, sort, filter };
}

export const techOperationsService = new TechOperationsService();
